// Workflow spec <-> canvas graph (React Flow nodes/edges), kept pure so it is fully testable.
//
// The SPEC is the source of truth (the backend validates and runs it); the canvas is a view
// of it plus node positions. Edges of agent/parallel/human nodes are the spec's edges; a
// router's outgoing edges are derived from its routes (labelled with the condition) and its
// default, and are edited in the router's inspector, not by dragging.

export const NODE_KINDS = ["agent", "parallel", "router", "human", "end"];
export const OPERATORS = ["eq", "ne", "contains", "not_contains", "gt", "gte", "lt", "lte", "exists", "not_exists", "in"];
export const CHANNEL_TYPES = ["string", "number", "boolean", "array", "object"];
export const REDUCERS = ["replace", "append", "merge"];

const X_GAP = 260;
const Y_GAP = 140;

export function emptySpec() {
  return {
    channels: { question: { type: "string", reducer: "replace" }, answer: { type: "string", reducer: "replace" } },
    input_channel: "question",
    output_channel: "answer",
    entry: "",
    nodes: {},
    edges: [],
    max_node_visits: 1,
  };
}

export function defaultNode(kind) {
  switch (kind) {
    case "agent": return { type: "agent", agent_id: "", task: "{{question}}", output: "answer", on_error: "fail" };
    case "parallel": return { type: "parallel", branches: [
      { agent_id: "", task: "{{question}}", output: "answer", on_error: "fail" },
      { agent_id: "", task: "{{question}}", output: "answer", on_error: "fail" }] };
    case "router": return { type: "router", routes: [], default: "" };
    case "human": return { type: "human", prompt: "Approve {{answer}}?", output: "answer" };
    default: return { type: "end" };
  }
}

/** A node id not yet used in the spec ("agent_1", "agent_2", ...). */
export function freshNodeId(spec, kind) {
  let n = 1;
  while (spec.nodes[`${kind}_${n}`]) n += 1;
  return `${kind}_${n}`;
}

function conditionLabel(when) {
  if (!when) return "";
  return ["exists", "not_exists"].includes(when.op) ? `${when.channel} ${when.op}`
    : `${when.channel} ${when.op} ${JSON.stringify(when.value)}`;
}

/** Edges as the canvas shows them: spec edges + router routes/default. */
export function graphEdges(spec) {
  const edges = (spec.edges || []).map((e) => ({ id: `e:${e.from}->${e.to}`, source: e.from, target: e.to }));
  Object.entries(spec.nodes || {}).forEach(([id, node]) => {
    if (node.type !== "router") return;
    (node.routes || []).forEach((route, i) => {
      if (route.to) edges.push({ id: `r:${id}:${i}`, source: id, target: route.to, label: conditionLabel(route.when),
        data: { router: true } });
    });
    if (node.default) edges.push({ id: `r:${id}:default`, source: id, target: node.default, label: "default",
      data: { router: true } });
  });
  return edges;
}

/** Layered layout (breadth-first from the entry) for nodes without a stored position. */
export function layout(spec, positions = {}) {
  const depth = {};
  const order = [];
  const queue = spec.entry && spec.nodes[spec.entry] ? [spec.entry] : [];
  if (queue.length) depth[spec.entry] = 0;
  const next = (id) => graphEdges(spec).filter((e) => e.source === id).map((e) => e.target);
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    next(id).forEach((target) => {
      if (depth[target] === undefined && spec.nodes[target]) {
        depth[target] = depth[id] + 1;
        queue.push(target);
      }
    });
  }
  Object.keys(spec.nodes || {}).forEach((id) => {
    if (depth[id] === undefined) { depth[id] = order.length; order.push(id); }
  });
  const perLevel = {};
  return Object.fromEntries(Object.keys(spec.nodes || {}).map((id) => {
    if (positions[id]) return [id, positions[id]];
    const level = depth[id];
    const slot = perLevel[level] || 0;
    perLevel[level] = slot + 1;
    return [id, { x: slot * X_GAP, y: level * Y_GAP }];
  }));
}

/** React Flow nodes for the spec. */
export function graphNodes(spec, positions = {}, agentNames = {}) {
  const placed = layout(spec, positions);
  return Object.entries(spec.nodes || {}).map(([id, node]) => ({
    id,
    position: placed[id],
    data: { label: nodeLabel(id, node, agentNames), kind: node.type, entry: id === spec.entry },
    type: "default",
    className: `wf-node wf-node--${node.type}${id === spec.entry ? " wf-node--entry" : ""}`,
  }));
}

export function nodeLabel(id, node, agentNames = {}) {
  if (node.type === "agent") return `${id} · ${agentNames[node.agent_id] || node.agent_id || "?"}`;
  if (node.type === "parallel") return `${id} · ${(node.branches || []).length}×`;
  return `${id} · ${node.type}`;
}

/** Connect ``source -> target`` in the spec (a non-router node has exactly one outgoing edge). */
export function connect(spec, source, target) {
  const node = spec.nodes[source];
  if (!node || !spec.nodes[target] || node.type === "end" || node.type === "router" || source === target) {
    return spec;
  }
  const edges = (spec.edges || []).filter((e) => e.from !== source);
  return { ...spec, edges: [...edges, { from: source, to: target }] };
}

/** Remove a node and every reference to it (edges, routes, defaults, entry). */
export function removeNode(spec, id) {
  const nodes = { ...spec.nodes };
  delete nodes[id];
  Object.entries(nodes).forEach(([key, node]) => {
    if (node.type === "router") {
      nodes[key] = { ...node, routes: (node.routes || []).filter((r) => r.to !== id),
        default: node.default === id ? "" : node.default };
    }
  });
  return { ...spec, nodes, edges: (spec.edges || []).filter((e) => e.from !== id && e.to !== id),
    entry: spec.entry === id ? "" : spec.entry };
}

/** Agent ids a spec uses (for the "which agents" summary and access hints). */
export function usedAgents(spec) {
  const ids = [];
  Object.values(spec.nodes || {}).forEach((node) => {
    const steps = node.type === "parallel" ? node.branches || [] : node.type === "agent" ? [node] : [];
    steps.forEach((s) => { if (s.agent_id && !ids.includes(s.agent_id)) ids.push(s.agent_id); });
  });
  return ids;
}
